import React, { useEffect, useState } from "react";
import {
  sendSystemMessage
} from "../utils/sendSystemMessage";
import {
  uploadMedia
} from "../utils/MediaUtils";

const MAX_PROOFS = 3;

/* Only keyframe animations — everything else is Tailwind */
const animations = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@400;500&display=swap');

  * { font-family: 'Syne', sans-serif; }
  .font-mono-dm { font-family: 'DM Mono', monospace !important; }

  @keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(0.75); }
  }
  @keyframes fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes slide-up {
    from { transform: translateY(40px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
  @keyframes scale-in {
    from { transform: scale(0.92); opacity: 0; }
    to   { transform: scale(1);    opacity: 1; }
  }

  .animate-pulse-dot  { animation: pulse-dot  1.2s ease-in-out infinite; }
  .animate-fade-in    { animation: fade-in    0.18s ease; }
  .animate-slide-up   { animation: slide-up   0.22s cubic-bezier(0.34,1.56,0.64,1); }
  .animate-scale-in   { animation: scale-in   0.20s cubic-bezier(0.34,1.56,0.64,1); }
`;

export default function OrderPayment({
  socket,
  order,
  wallet,
  remainingTime = 0,
  paymentDetails = [],
  onOpenChat,
   sendMessage, 
   setPendingMedia,
  onBackToMarket,
  myChatId,
   showToast,
  getUserKey,
   setMessages,
    setUsers,
  uuid,
}) {
  const [showProofModal,  setShowProofModal]  = useState(false);
  const [showFollowModal, setShowFollowModal] = useState(false);
  const [followLoading,   setFollowLoading]   = useState(false);
  const [checkingFollow,  setCheckingFollow]  = useState(false);

  const [confirmedOwnAccount, setConfirmedOwnAccount] = useState(false);
  const [selectedFiles,       setSelectedFiles]       = useState([]);
  const [previews,            setPreviews]            = useState([]);
  const [uploading,           setUploading]           = useState(false);
  const [progress,            setProgress]            = useState(0);

  /* ── TIMER ── */
  const safeRemaining = Number.isFinite(remainingTime) ? remainingTime : 0;
  const minutes  = Math.floor(safeRemaining / 60);
  const seconds  = safeRemaining % 60;
  const isUrgent = safeRemaining > 0 && safeRemaining < 60;

  const status = order?.status ?? "pending";

  /* ── JOIN ORDER ROOM ── */
  useEffect(() => {
    if (!socket || !order?.orderId) return;
    socket.emit("p2p:join-order", { orderId: order.orderId });
  }, [socket, order?.orderId]);


  /* ── FOLLOW CHECK RESULT ── */
  useEffect(() => {
    if (!socket) return;
    const onResult = ({ sellerChatId, canChat }) => {
      setCheckingFollow(false);
      if (String(sellerChatId) !== String(order?.sellerChatId)) return;
      canChat ? onOpenChat?.() : setShowFollowModal(true);
    };
    socket.on("p2p:check-seller-follow:result", onResult);
    return () => socket.off("p2p:check-seller-follow:result", onResult);
  }, [socket, order?.sellerChatId, onOpenChat]);

  /* ── FOLLOW SUCCESS ── */
  useEffect(() => {
    if (!socket) return;
    const onFollowSuccess = ({ targetChatId }) => {
      if (String(targetChatId) !== String(order?.sellerChatId)) return;
      setFollowLoading(false);
      setShowFollowModal(false);
      onOpenChat?.();
    };
    socket.on("feed-follow-success", onFollowSuccess);
    return () => socket.off("feed-follow-success", onFollowSuccess);
  }, [socket, order?.sellerChatId, onOpenChat]);

  /* ── HANDLERS ── */
  const handleChatSeller = () => {
  
    if (!socket || !order?.sellerChatId || checkingFollow) return;
    setCheckingFollow(true);
    socket.emit("p2p:check-seller-follow", { sellerChatId: order.sellerChatId });
  };

  const handleFollowSeller = () => {
    if (!socket || !order?.sellerChatId || followLoading) return;
    setFollowLoading(true);
    socket.emit("feed-follow-user", { targetChatId: order.sellerChatId });
  };

  const onSelectFiles = (files) => {
    const list = Array.from(files || []);
    if (list.length + selectedFiles.length > MAX_PROOFS) {
      alert(`You can upload up to ${MAX_PROOFS} images`);
      return;
    }
    setSelectedFiles((prev) => [...prev, ...list]);
    setPreviews((prev) => [...prev, ...list.map((f) => URL.createObjectURL(f))]);
  };

  const confirmPaid = async () => {

  if (
    !confirmedOwnAccount ||
    !selectedFiles.length
  ) {
    return;
  }

  try {

    setUploading(true);

    /* ----------------------------------------------------
       1. SEND SYSTEM MESSAGE (ONCE)
    ---------------------------------------------------- */

    const systemResult =
    await sendSystemMessage({

      socket,

      myChatId,

      recipientChatId:
        order.sellerChatId,

      senderText:
`You have marked Order #${order.orderId} as PAID.

Please wait for the seller to verify your payment and release the asset.`,

      recipientText:
`The buyer has marked Order #${order.orderId} as PAID.

Kindly verify the payment and release the funds if payment has been received.`,

      system: true,

      uploadMedia,

      getUserKey,

      setMessages,
      setUsers,

      showToast,

      uuid,

    });

    if (!systemResult) {

      throw new Error(
        "System message failed"
      );

    }

    /* ----------------------------------------------------
       2. SEND PAYMENT PROOF IMAGES
    ---------------------------------------------------- */

    for (
      let i = 0;
      i < selectedFiles.length;
      i++
    ) {

      const result =
      await sendSystemMessage({

        socket,

        myChatId,

        recipientChatId:
          order.sellerChatId,

        file:
          selectedFiles[i],

        uploadMedia,

        setIsUploading:
          setUploading,

        setUploadProgress:
          setProgress,

        getUserKey,

        setMessages,
        setUsers,

        showToast,

        uuid,

      });

      if (!result) {

        throw new Error(
          "Payment proof send failed"
        );

      }

    }

    /* ----------------------------------------------------
       P2P ORDER STATE UPDATE
    ---------------------------------------------------- */
socket.emit(
  "p2p:order-paid",
  {
    orderId: order.orderId,
  }
);

    setShowProofModal(false);
    setSelectedFiles([]);
    setPreviews([]);
    setConfirmedOwnAccount(false);

  } catch (err) {

    alert(
      "Failed to upload payment proof"
    );

  } finally {

    setUploading(false);
    setProgress(0);

  }

};
  /* ── STATUS BADGE STYLES ── */
  const badgeClass = {
    pending:   "bg-amber-50 text-amber-600 border border-amber-200",
    paid:      "bg-emerald-50 text-emerald-600 border border-emerald-200",
    expired:   "bg-red-50 text-red-600 border border-red-200",
    completed: "bg-emerald-50 text-emerald-600 border border-emerald-200",
  }[status] ?? "bg-gray-100 text-gray-500 border border-gray-200";

const sellerDisplayName = order?.displayName || order?.sellerUsername || order?.sellerChatId || "Seller";
const sellerInitial = sellerDisplayName[0].toUpperCase();

  /* ── PAYMENT DETAILS: normalise to array whether prop is array or plain object ── */
  const normalisedPaymentDetails = React.useMemo(() => {
    if (!paymentDetails) return [];
    if (Array.isArray(paymentDetails)) return paymentDetails;
    // object shape: { "MTN Mobile Money": "0241953789", … }
    return Object.entries(paymentDetails)
      .filter(([, v]) => v)
      .map(([type, details]) => ({ type, details, instruction: "" }));
  }, [paymentDetails]);

  return (
    <>
      <style>{animations}</style>

      {/* ── ROOT ── */}
      <div className="h-full min-h-0 flex-1 flex flex-col bg-white text-[#111318] overflow-hidden relative">

              {/* ── HEADER ── */}
              <div
                className="flex items-center justify-between px-5 py-4 border-b border-black/[0.08] bg-white shrink-0 z-10"
                style={{ paddingTop: "max(16px, env(safe-area-inset-top, 16px))" }}
              >
          <button
  onClick={onBackToMarket}
  className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:opacity-60 transition-colors"
  aria-label="Back"
>
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
</button>

          <span className="text-[13px] font-bold tracking-[0.1em] uppercase text-[#111318]">
            Order
          </span>

          {/* Timer */}
          <div className="flex items-center gap-1.5 bg-[#f1f3f7] border border-black/[0.08] rounded-lg px-3 py-1.5">
            <span
              className={`w-[7px] h-[7px] rounded-full animate-pulse-dot ${
                isUrgent ? "bg-red-600" : "bg-emerald-500"
              }`}
            />
            <span
              className={`font-mono-dm text-sm font-medium tracking-[0.05em] min-w-[42px] text-right ${
                isUrgent ? "text-red-600" : "text-emerald-500"
              }`}
            >
              {minutes}:{seconds.toString().padStart(2, "0")}
            </span>
          </div>
        </div>

        {/* ── SCROLLABLE BODY ── pb-[120px] reserves space for the fixed footer.
              min-h-0 is required so this flex child can actually shrink and
              become scrollable instead of growing to fit its content. */}
        <div
          className="
            flex-1
            min-h-0
            overflow-y-auto
            px-4
            pt-5
            pb-[120px]
            flex
            flex-col
            gap-4
          "
        >

          {/* ORDER SUMMARY CARD */}
          <div className="bg-[#f8f9fb] border border-black/[0.08] rounded-2xl overflow-visible">
            <div className="font-mono-dm text-[10px] font-medium tracking-[0.12em] uppercase text-[#8a92a3] px-4 py-3 border-b border-black/[0.08]">
              Order Summary
            </div>

            {/* Amount + Committed */}
            <div className="grid grid-cols-2">
              <div className="p-4 flex flex-col gap-1">
                <span className="font-mono-dm text-[10px] text-[#8a92a3] tracking-[0.08em] uppercase">Order Amount</span>
                <span className="text-[22px] font-extrabold text-emerald-500 leading-tight tracking-tight">
                  {order?.amount ?? "—"}
                </span>
                <span className="font-mono-dm text-[10px] text-[#8a92a3] mt-0.5">{order?.asset ?? "USWD"}</span>
              </div>
              <div className="p-4 flex flex-col gap-1 border-l border-black/[0.08]">
                <span className="font-mono-dm text-[10px] text-[#8a92a3] tracking-[0.08em] uppercase">
                  Committed
                </span>
                <span className="text-[22px] font-extrabold text-[#111318] leading-tight tracking-tight">
                  {order?.amount ?? 0}
                </span>
                <span className="font-mono-dm text-[10px] text-[#8a92a3] mt-0.5">
                  {order?.asset ?? "USWD"}
                </span>
              </div>
            </div>

            {/* Rate + You Pay */}
            {order?.rate != null && (
              <div className="grid grid-cols-2 border-t border-black/[0.08]">
                <div className="p-4 flex flex-col gap-1">
                  <span className="font-mono-dm text-[10px] text-[#8a92a3] tracking-[0.08em] uppercase">Rate</span>
                  <span className="text-[17px] font-extrabold text-[#111318] leading-tight tracking-tight">
                    {order.rate}
                  </span>
                  <span className="font-mono-dm text-[10px] text-[#8a92a3] mt-0.5">per USWD</span>
                </div>
                <div className="p-4 flex flex-col gap-1 border-l border-black/[0.08]">
                  <span className="font-mono-dm text-[10px] text-[#8a92a3] tracking-[0.08em] uppercase">You Pay</span>
                  <span className="text-[17px] font-extrabold text-emerald-500 leading-tight tracking-tight">
                    {order?.amount && order?.rate
                      ? (order.amount * order.rate).toLocaleString()
                      : "—"}
                  </span>
                  <span className="font-mono-dm text-[10px] text-[#8a92a3] mt-0.5">local currency</span>
                </div>
              </div>
            )}

            {/* Order ID + Status */}
            <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-4 min-h-[64px] border-t border-black/[0.08]">
              <div>
                <div className="font-mono-dm text-[10px] text-[#8a92a3] tracking-[0.08em] uppercase">Order ID</div>
                <div className="font-mono-dm text-[12px] text-[#111318] tracking-[0.04em]">
                  #{order?.orderId ?? "—"}
                </div>
              </div>
              <span
                className={`
                  inline-flex items-center gap-1.5
                  px-3 py-1.5 rounded-full
                  font-mono-dm text-[10px] font-medium tracking-[0.08em] uppercase
                  shrink-0 whitespace-nowrap
                  ${badgeClass}
                `}
              >
                <span className="w-[5px] h-[5px] rounded-full bg-current inline-block" />
                {status}
              </span>
            </div>
          </div>

          {/* PAYMENT DETAILS CARD */}
          <div className="bg-[#f8f9fb] border border-black/[0.08] rounded-2xl">
            <div className="font-mono-dm text-[10px] font-medium tracking-[0.12em] uppercase text-[#8a92a3] px-4 py-3 border-b border-black/[0.08] rounded-t-2xl">
              Send Payment To
            </div>
            {normalisedPaymentDetails.length === 0 ? (
              <div className="px-4 py-5 font-mono-dm text-xs text-[#8a92a3] text-center tracking-[0.04em] rounded-b-2xl">
                No payment details provided
              </div>
            ) : (
              <div className="rounded-b-2xl overflow-hidden">
                {normalisedPaymentDetails.map((p, i) => (
                  <div
                    key={i}
                    className={`px-4 py-3.5 flex flex-col gap-1 ${
                      i < normalisedPaymentDetails.length - 1 ? "border-b border-black/[0.08]" : ""
                    }`}
                  >
                    <span className="font-mono-dm text-[10px] tracking-[0.1em] uppercase text-emerald-500 font-medium">
                      {p.type}
                    </span>
                    <span className="font-mono-dm text-sm text-[#111318] tracking-[0.02em] break-all">
                      {p.details}
                    </span>
                    {p.instruction && (
                      <span className="text-[11px] text-[#8a92a3] mt-0.5">{p.instruction}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* WALLET CARD */}
          {wallet?.available != null && (
            <div className="bg-[#f8f9fb] border border-black/[0.08] rounded-2xl overflow-hidden">
              <div className="font-mono-dm text-[10px] font-medium tracking-[0.12em] uppercase text-[#8a92a3] px-4 py-3 border-b border-black/[0.08]">
                Wallet
              </div>
              <div className="grid grid-cols-2">
                <div className="p-4 flex flex-col gap-1">
                  <span className="font-mono-dm text-[10px] text-[#8a92a3] tracking-[0.08em] uppercase">Available</span>
                  <span className="text-[18px] font-extrabold text-[#111318] leading-tight">{wallet.available}</span>
                  <span className="font-mono-dm text-[10px] text-[#8a92a3] mt-0.5">USWD</span>
                </div>
                <div className="p-4 flex flex-col gap-1 border-l border-black/[0.08]">
                  <span className="font-mono-dm text-[10px] text-[#8a92a3] tracking-[0.08em] uppercase">Balance</span>
                  <span className="text-[18px] font-extrabold text-[#111318] leading-tight">{wallet.balance ?? 0}</span>
                  <span className="font-mono-dm text-[10px] text-[#8a92a3] mt-0.5">USWD</span>
                </div>
              </div>
            </div>
          )}

        </div>{/* end scrollable body */}

                {/* ── FIXED FOOTER ── */}
        <div
          className="absolute bottom-0 left-0 right-0 bg-white border-t border-black/[0.08] px-4 py-3 flex flex-row gap-2 z-10"
          style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom, 12px))" }}
        >
          <button
            onClick={() => setShowProofModal(true)}
            disabled={status === "expired" || status === "completed"}
            className="flex-1 bg-emerald-500 text-white border-none rounded-xl py-3.5 text-[13px] font-bold tracking-[0.03em] cursor-pointer transition-all active:scale-[0.98] disabled:opacity-35 disabled:cursor-not-allowed"
          >
            📸 Proof
          </button>

          <button
            onClick={handleChatSeller}
            disabled={checkingFollow}
            className="flex-1 bg-[#f8f9fb] text-[#111318] border border-black/[0.08] rounded-xl py-3.5 text-[13px] font-semibold cursor-pointer transition-colors hover:border-emerald-500 disabled:opacity-45 disabled:cursor-not-allowed"
          >
            {checkingFollow ? "Checking…" : "💬 Chat Seller"}
          </button>
        </div>

      </div>{/* end root */}

      {/* ── PROOF UPLOAD MODAL (bottom-sheet) ── */}
      {showProofModal && (
       <div
  className="
    fixed inset-0
    bg-black/45
    z-[100]

    flex
    items-end
    md:items-center

    justify-center

    animate-fade-in
  "
  onClick={(e) =>
    e.target === e.currentTarget &&
    setShowProofModal(false)
  }
>
          <div
  className="
    bg-white
    border border-black/[0.08]

    w-full
    h-full

    md:h-auto
    md:w-[520px]
    md:max-h-[90vh]

    overflow-y-auto

    rounded-none
    md:rounded-2xl

    px-5
    pt-6

    flex
    flex-col
    gap-4

    animate-slide-up
  "
  style={{ paddingBottom: "max(36px, env(safe-area-inset-bottom, 36px))" }}
>
            {/* Handle */}
            <div className="w-10 h-1 bg-black/[0.08] rounded-full mx-auto -mt-2 mb-1" />
            <h2 className="text-[17px] font-extrabold tracking-tight text-center text-[#111318]">
              Upload Payment Proof
            </h2>

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200/60 rounded-xl px-3.5 py-3 text-xs text-amber-600 leading-[1.55] tracking-[0.01em]">
              ⚠️ Do not pay from a third-party account. Using another person's account may result in suspension.
            </div>

            {/* Checkbox */}
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmedOwnAccount}
                onChange={(e) => setConfirmedOwnAccount(e.target.checked)}
                className="w-[18px] h-[18px] accent-emerald-500 shrink-0 mt-0.5"
              />
              <span className="text-[13px] text-[#111318] leading-[1.45]">
                I have paid using my own account
              </span>
            </label>

            {/* File select */}
            <label className="flex items-center justify-center gap-2 border border-dashed border-emerald-400/60 bg-emerald-500/10 text-emerald-600 rounded-xl py-3.5 text-[13px] font-semibold cursor-pointer hover:border-emerald-500 transition-colors">
              📎 Select Images ({selectedFiles.length}/{MAX_PROOFS})
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => onSelectFiles(e.target.files)}
              />
            </label>

            {/* Preview grid */}
            {previews.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {previews.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`Proof ${i + 1}`}
                    className="w-full aspect-square object-cover rounded-lg border border-black/[0.08]"
                  />
                ))}
              </div>
            )}

            {/* Progress */}
            {uploading && (
              <div>
                <div className="h-1 bg-[#f1f3f7] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="font-mono-dm text-[10px] text-[#8a92a3] text-right mt-1 tracking-[0.06em]">
                  Uploading… {progress}%
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-2 mt-1">
              <button
                disabled={uploading || !confirmedOwnAccount || selectedFiles.length === 0}
                onClick={confirmPaid}
                className="w-full bg-emerald-500 text-white border-none rounded-xl py-4 text-sm font-bold tracking-[0.04em] cursor-pointer transition-all active:scale-[0.98] disabled:opacity-35 disabled:cursor-not-allowed"
              >
                ✅ I have paid — Notify Seller
              </button>
              <button
                onClick={() => setShowProofModal(false)}
                className="w-full bg-transparent border border-black/[0.08] text-[#8a92a3] rounded-xl py-3.5 text-[13px] font-semibold cursor-pointer hover:border-black/25 hover:text-[#111318] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── FOLLOW SELLER MODAL (centered card) ── */}
      {showFollowModal && (
        <div
          className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center animate-fade-in"
          onClick={(e) => e.target === e.currentTarget && setShowFollowModal(false)}
        >
          <div className="bg-white border border-black/[0.08] rounded-2xl p-6 w-[min(420px,calc(100vw-32px))] flex flex-col gap-4 shadow-[0_20px_60px_rgba(0,0,0,0.25)] animate-scale-in">
            {/* Avatar */}
            <div className="w-[52px] h-[52px] rounded-full bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center text-[22px] font-extrabold text-emerald-500 mx-auto">
              {sellerInitial}
            </div>

            <h2 className="text-[17px] font-extrabold tracking-tight text-center text-[#111318]">
             Follow {sellerDisplayName} to Chat
            </h2>

            {/* Info box */}
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3.5 py-3 text-xs text-emerald-800 leading-[1.55] tracking-[0.01em] text-center">
              You need to follow this seller before you can open a direct message.
              Following also lets you see their future ads.
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <button
                disabled={followLoading}
                onClick={handleFollowSeller}
                className={`w-full bg-emerald-500 text-white border-none rounded-xl py-4 text-sm font-bold tracking-[0.04em] cursor-pointer transition-all active:scale-[0.98] ${
                  followLoading ? "opacity-65 cursor-not-allowed" : ""
                }`}
              >
                {followLoading ? "Following…" : "➕ Follow Seller & Open Chat"}
              </button>
              <button
                onClick={() => setShowFollowModal(false)}
                className="w-full bg-transparent border border-black/[0.08] text-[#8a92a3] rounded-xl py-3.5 text-[13px] font-semibold cursor-pointer hover:border-black/25 hover:text-[#111318] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}